import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText,
  MessageSquare,
  LogOut,
  Plus,
  Edit3,
  Trash2,
  Eye,
  EyeOff,
  Save,
  X,
  LayoutDashboard,
  Phone,
  Mail,
  Calendar,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";

const AdminDashboard = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    category: "Conseil",
    image: "",
    published: true,
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === "articles") {
        const res = await api.get("/admin/articles");
        setArticles(res.data);
      } else if (activeTab === "contacts") {
        const res = await api.get("/contacts");
        setContacts(res.data);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      if (err.response?.status === 401) {
        logout();
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const resetForm = () => {
    setFormData({
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      category: "Conseil",
      image: "",
      published: true,
    });
    setEditingArticle(null);
    setShowForm(false);
  };

  const handleEditArticle = (article) => {
    setFormData({
      title: article.title,
      slug: article.slug,
      excerpt: article.excerpt,
      content: article.content,
      category: article.category,
      image: article.image,
      published: article.published,
    });
    setEditingArticle(article);
    setShowForm(true);
  };

  const handleSaveArticle = async () => {
    try {
      if (editingArticle) {
        await api.put(`/admin/articles/${editingArticle.id}`, formData);
      } else {
        await api.post("/admin/articles", formData);
      }
      resetForm();
      fetchData();
    } catch (err) {
      console.error("Error saving article:", err);
      alert("Erreur lors de la sauvegarde");
    }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    try {
      await api.delete(`/admin/articles/${id}`);
      fetchData();
    } catch (err) {
      console.error("Error deleting article:", err);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] pt-24">
      {/* Header */}
      <div className="bg-white border-b border-[#E8DDD3]/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="w-5 h-5 text-[#C4956A]" />
            <h1 className="text-lg font-bold text-[#1A1A2E]">Administration</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#1A1A2E]/50">{admin?.email}</span>
            <Link to="/" className="text-sm text-[#C4956A] hover:text-[#B38459] transition-colors">
              Voir le site
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
        <div className="flex gap-2 mb-6">
          {[
            { key: "articles", label: "Articles", icon: FileText },
            { key: "contacts", label: "Messages", icon: MessageSquare },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.key
                  ? "bg-[#C4956A] text-white"
                  : "bg-white text-[#1A1A2E]/60 hover:bg-[#C4956A]/5 border border-[#E8DDD3]/50"
              }`}
            >
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* Articles Tab */}
        {activeTab === "articles" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#1A1A2E]">Gestion des articles</h2>
              <button
                onClick={() => { resetForm(); setShowForm(true); }}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#C4956A] text-white text-sm font-medium rounded-lg hover:bg-[#B38459] transition-colors"
              >
                <Plus className="w-4 h-4" /> Nouvel article
              </button>
            </div>

            {/* Article Form */}
            {showForm && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl border border-[#E8DDD3]/50 p-6 mb-6"
              >
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-bold text-[#1A1A2E]">
                    {editingArticle ? "Modifier l'article" : "Nouvel article"}
                  </h3>
                  <button onClick={resetForm} className="p-1 hover:bg-[#FAF9F6] rounded-lg">
                    <X className="w-5 h-5 text-[#1A1A2E]/50" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#1A1A2E]/60 mb-1 uppercase tracking-wider">Titre *</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="w-full px-4 py-2.5 bg-[#FAF9F6] border border-[#E8DDD3] rounded-lg text-sm focus:outline-none focus:border-[#C4956A]"
                        placeholder="Titre de l'article"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#1A1A2E]/60 mb-1 uppercase tracking-wider">Slug</label>
                      <input
                        type="text"
                        value={formData.slug}
                        onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                        className="w-full px-4 py-2.5 bg-[#FAF9F6] border border-[#E8DDD3] rounded-lg text-sm focus:outline-none focus:border-[#C4956A]"
                        placeholder="url-de-l-article (auto si vide)"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#1A1A2E]/60 mb-1 uppercase tracking-wider">Catégorie</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-4 py-2.5 bg-[#FAF9F6] border border-[#E8DDD3] rounded-lg text-sm focus:outline-none focus:border-[#C4956A] appearance-none"
                      >
                        <option>Conseil</option>
                        <option>Analyse de marché</option>
                        <option>Fiscalité</option>
                        <option>Rénovation</option>
                        <option>Guide</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#1A1A2E]/60 mb-1 uppercase tracking-wider">Image URL</label>
                      <input
                        type="text"
                        value={formData.image}
                        onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                        className="w-full px-4 py-2.5 bg-[#FAF9F6] border border-[#E8DDD3] rounded-lg text-sm focus:outline-none focus:border-[#C4956A]"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#1A1A2E]/60 mb-1 uppercase tracking-wider">Extrait *</label>
                    <textarea
                      value={formData.excerpt}
                      onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                      rows={2}
                      className="w-full px-4 py-2.5 bg-[#FAF9F6] border border-[#E8DDD3] rounded-lg text-sm focus:outline-none focus:border-[#C4956A] resize-none"
                      placeholder="Court résumé de l'article"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#1A1A2E]/60 mb-1 uppercase tracking-wider">Contenu HTML *</label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      rows={10}
                      className="w-full px-4 py-2.5 bg-[#FAF9F6] border border-[#E8DDD3] rounded-lg text-sm focus:outline-none focus:border-[#C4956A] resize-y font-mono"
                      placeholder="<h2>Titre</h2><p>Paragraphe...</p>"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.published}
                        onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                        className="w-4 h-4 rounded border-[#E8DDD3] text-[#C4956A] focus:ring-[#C4956A]"
                      />
                      <span className="text-sm text-[#1A1A2E]/70">Publié</span>
                    </label>
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <button
                      onClick={handleSaveArticle}
                      className="flex items-center gap-2 px-6 py-2.5 bg-[#C4956A] text-white text-sm font-medium rounded-lg hover:bg-[#B38459] transition-colors"
                    >
                      <Save className="w-4 h-4" /> Sauvegarder
                    </button>
                    <button
                      onClick={resetForm}
                      className="px-6 py-2.5 text-sm text-[#1A1A2E]/50 hover:text-[#1A1A2E] transition-colors"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Articles List */}
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                    <div className="h-5 bg-[#E8DDD3]/30 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#E8DDD3]/50">
                <FileText className="w-10 h-10 text-[#1A1A2E]/20 mx-auto" />
                <p className="text-[#1A1A2E]/40 mt-3">Aucun article</p>
              </div>
            ) : (
              <div className="space-y-3">
                {articles.map((article) => (
                  <div
                    key={article.id}
                    className="bg-white rounded-xl border border-[#E8DDD3]/50 p-4 flex items-center justify-between hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {article.image && (
                        <img
                          src={article.image}
                          alt=""
                          className="w-16 h-12 object-cover rounded-lg flex-shrink-0"
                        />
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-[#1A1A2E] text-sm truncate">{article.title}</h3>
                          {!article.published && (
                            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] font-medium rounded-full flex-shrink-0">
                              Brouillon
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-[#C4956A]">{article.category}</span>
                          <span className="text-xs text-[#1A1A2E]/30">{formatDate(article.date)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-4">
                      <Link
                        to={`/ressources/${article.slug}`}
                        target="_blank"
                        className="p-2 hover:bg-[#FAF9F6] rounded-lg transition-colors"
                        title="Voir"
                      >
                        <Eye className="w-4 h-4 text-[#1A1A2E]/40" />
                      </Link>
                      <button
                        onClick={() => handleEditArticle(article)}
                        className="p-2 hover:bg-[#FAF9F6] rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Edit3 className="w-4 h-4 text-[#C4956A]" />
                      </button>
                      <button
                        onClick={() => handleDeleteArticle(article.id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === "contacts" && (
          <div>
            <h2 className="text-xl font-bold text-[#1A1A2E] mb-6">Messages reçus</h2>
            {loading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                    <div className="h-5 bg-[#E8DDD3]/30 rounded w-1/3" />
                  </div>
                ))}
              </div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#E8DDD3]/50">
                <MessageSquare className="w-10 h-10 text-[#1A1A2E]/20 mx-auto" />
                <p className="text-[#1A1A2E]/40 mt-3">Aucun message</p>
              </div>
            ) : (
              <div className="space-y-3">
                {contacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="bg-white rounded-xl border border-[#E8DDD3]/50 p-5"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-[#1A1A2E]">
                          {contact.firstName} {contact.lastName}
                        </h3>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="flex items-center gap-1 text-xs text-[#1A1A2E]/50">
                            <Mail className="w-3 h-3" /> {contact.email}
                          </span>
                          {contact.phone && (
                            <span className="flex items-center gap-1 text-xs text-[#1A1A2E]/50">
                              <Phone className="w-3 h-3" /> {contact.phone}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {contact.subject && (
                          <span className="px-2 py-1 bg-[#C4956A]/10 text-[#C4956A] text-[10px] font-medium rounded-full">
                            {contact.subject}
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-[10px] text-[#1A1A2E]/30">
                          <Calendar className="w-3 h-3" /> {formatDate(contact.created_at)}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-[#1A1A2E]/70 mt-3 leading-relaxed bg-[#FAF9F6] p-3 rounded-lg">
                      {contact.message}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
